import React, { useEffect, useState } from 'react';
import { ExternalLink } from "react-feather";
import { CameraFilled, ChartPieFilled } from '@launchmetrics/lm-icons';
import { Tooltip } from '@launchmetrics/lm-ui';
import NumeralMetric from "components/LM/common/NumeralMetric";
import Icons from 'components/LM/common/Icons';
import Item from "components/LM/Voice/Item";
import K2Api from 'lib/ajax/K2Api';

import 'components/LM/Voice/Profile.scss';

const K2_API = new K2Api();

const AUDIENCE_METRIC = {
	facebook: 'likes',
	instagram: 'followers',
	linkedin: 'connections',
	tiktok: 'followers',
	twitter: 'followers',
	youtube: 'subscribers',
    douyin: 'followers',
    wechat_public: 'followers',
    weibo: 'followers',
    xiaohongshu: 'followers',
	bilibili: 'followers'
};

function Profile(props) {
	const { profile, type, onToggle, checked, ...otherProps } = props;
	let audience;
	if ('audience' in profile)  {
		audience = (<p className="details text-black-50 mb-0"><NumeralMetric>{profile.audience}</NumeralMetric> {AUDIENCE_METRIC[type]}</p>);
	}

	let error;
	if (profile.error) {
		error = (
			<p className="mb-0 text-danger">
				<em>Error: {profile.error}</em>
			</p>
		);
	}

	let warning;
	if (profile.warning) {
		warning = (
			<p className="mb-0 text-warning">
				<em>{profile.warning}</em>
			</p>
		);
	}

	const additionalInfo = [];

	if (profile?.crawling?.error_counter > 0) {
		additionalInfo.push({
			color: "warning",
			title: profile.crawling.error_counter + " Crawling errors",
			text: profile.crawling.error_counter + " Crawling errors",
		});
	}

	if (profile.api_errors_counter > 0) {
		additionalInfo.push({
			color: "warning",
			title: profile.api_errors_counter + " errors trying to update the profile data",
			text: profile.api_errors_counter + " Profile update errors"
		});
	}

	if (profile.type === "instagram") {
		if (profile.is_restricted) {
			additionalInfo.push({
				color: "warning",
				title: profile.restricted_reason,
				text: "Restricted"
			});
		}
		if (!profile.is_business) {
			additionalInfo.push({
				color: "warning",
				text: "Not business"
			});
		}
	}

	const isInstagram = profile.type === 'instagram';
	const [storiesCrawling, setStoriesCrawling] = useState(false);

	useEffect(() => {
		if (!isInstagram) return;

		K2_API.get(`/socialmedia/instagram/storiescrawling/${profile.id}`)
			.then(() => {
				 setStoriesCrawling(true);
			})
			.catch(() => {
				 setStoriesCrawling(false);
			});

	}, [isInstagram, profile.id]);

	const badgesInformation = [];
	additionalInfo.forEach((item, index) => {
		badgesInformation.push(<div key={index}>{item.text}</div>)
	});
	const tips = badgesInformation.length > 0 && (
		<Tooltip
			placement="top"
			content={<div className='text-left'>{badgesInformation}</div>}
		>
			<div className="icon-tips">
				<Icons type='exclamation' size="16" />
			</div>
		</Tooltip>
	);

	const demographicsBadge = profile.has_demographics && (
		<Tooltip
			placement="top"
			content="Demographics data available"
		>
			<div className="icon-demographics">
				<ChartPieFilled style={{ fontSize: '16px' }} />
			</div>
		</Tooltip>
	);

	const storiesBadge = storiesCrawling && (
		<Tooltip
			placement="top"
			content="Stories are being collected for this profile"
		>
			<div className="icon-stories">
				<CameraFilled style={{ fontSize: '16px' }} />
			</div>
		</Tooltip>
	);

	return (
		<Item
			id={`profile_${type}_${profile.id}`}
			className={`item profile profile_${type}`}
			type={type}
			profileId={profile.id}
			checked={checked}
			onToggleItem={onToggle}
			addedAt={profile.added_at}
			pictureUrl={profile.picture_url}
			{...otherProps}>
			<ProfileLink profile={profile} />
			<ProviderLink profileUrl={profile.profile_url} />
			<div className="icon-badges">
				{demographicsBadge}
				{storiesBadge}
				{tips}
			</div>
			{audience}
			{error}
			{warning}
		</Item>
	)
}

function ProfileLink({ profile }) {
	if (profile.type === 'linkedin') {
		return profile.name;
	}

	const profileName = profile?.crawling?.enabled === false ? (
		<span className='text-danger'>{profile.name} (inactive)</span>
	) : (
		<>{profile.name}</>
	);

	if (profile.type === 'twitter') {
		profile.crawling = {
			enabled: false,
			error_counter: 0
		};
	}

	const link = `/${profile.type}/profile?id=${profile.id}`;
	return (
        <Tooltip
            placement="top"
            content={profile.name}
        >
            <a href={link} className="name text-ellipsis-title" rel="noopener noreferrer"> {profileName}</a>
        </Tooltip>
	);
}

function ProviderLink({ profileUrl }) {
	return (
		<a href={profileUrl} target='_blank' className='external-link' rel='noopener noreferrer'>
			<ExternalLink className="feather align-middle icon" />
		</a>
	);
}

export default Profile;
